/**
 * product-main.js
 * Handles: gallery switching, variant selection, quantity stepper,
 *          add-to-cart feedback, URL state update
 */

(function () {
  'use strict';

  // ── Helpers ───────────────────────────────────────────────────
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) {
      return '0';
    }

    const fixed = number.toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const formatString = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);

    if (!match) {
      return String(cents / 100);
    }

    let value = '';

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents / 100, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents / 100, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents / 100, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents / 100, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents / 100, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents / 100, 2, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents / 100, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function getMoneyFormat(section) {
    if (typeof Currency !== 'undefined' && Currency.money_format) {
      return Currency.money_format;
    }
    if (window.Shopify && Shopify.money_format) {
      return Shopify.money_format;
    }
    return (section && section.dataset.moneyFormat) || '${{amount}}';
  }

  function getPresentmentCents(cents) {
    if (typeof Currency !== 'undefined' && typeof Currency.convert === 'function') {
      return Currency.convert(cents, '');
    }
    if (window.Shopify && Shopify.currency && Shopify.currency.rate) {
      return Math.round(cents * parseFloat(Shopify.currency.rate));
    }
    return cents;
  }

  function formatPrice(cents, moneyFormat) {
    const presentmentCents = getPresentmentCents(cents);

    if (typeof Currency !== 'undefined' && typeof Currency.formatMoney === 'function') {
      return Currency.formatMoney(presentmentCents, moneyFormat);
    }
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(presentmentCents, moneyFormat);
    }
    return formatMoney(presentmentCents, moneyFormat);
  }

  // ── Gallery ───────────────────────────────────────────────────
  function initGallery(section) {
    const thumbs   = qsa('.nb-thumb', section);
    const slides   = qsa('.nb-gallery__slide', section);
    const btnPrev  = qs('[data-gallery-prev]', section);
    const btnNext  = qs('[data-gallery-next]', section);
    let current    = 0;

    function goTo(index) {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
      slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      current = index;
    }

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => goTo(i));
    });

    if (btnPrev) btnPrev.addEventListener('click', () => goTo(current - 1));
    if (btnNext) btnNext.addEventListener('click', () => goTo(current + 1));

    // Touch/swipe support on main image
    const mainEl = qs('.nb-gallery__main', section);
    if (mainEl) {
      let startX = 0;
      mainEl.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
      mainEl.addEventListener('touchend', e => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) goTo(diff > 0 ? current + 1 : current - 1);
      });
    }

    return { goTo };
  }

  // ── Variants ──────────────────────────────────────────────────
  function initVariants(section, gallery) {
    const form       = qs('.nb-product__form', section);
    if (!form) return;

    const sectionId  = section.dataset.sectionId;
    const jsonEl     = document.getElementById('ProductJson-' + sectionId);
    if (!jsonEl) return;

    let variants;
    try { variants = JSON.parse(jsonEl.textContent); }
    catch (e) { console.warn('NB: could not parse product JSON'); return; }

    const priceEl      = qs('[data-product-price]', section);
    const comparePriceEl = qs('[data-compare-price]', section);
    const variantIdEl  = qs('[data-variant-id]', section);
    const atcBtn       = qs('[data-add-to-cart]', section);
    const atcText      = qs('[data-add-to-cart-text]', section);
    const colorValue   = qs('[data-selected-color]', section);
    const sizeValue    = qs('[data-selected-size]', section);

    function getSelectedOptions() {
      const options = [];
      qsa('[data-option-index]', form).forEach(el => {
        if (el.tagName === 'SELECT') {
          options[parseInt(el.dataset.optionIndex)] = el.value;
        }
      });
      qsa('input[type="radio"]:checked', form).forEach(input => {
        const wrap = input.closest('[data-option-index]');
        if (wrap) options[parseInt(wrap.dataset.optionIndex)] = input.value;
      });
      return options;
    }

    function findVariant(selectedOptions) {
      return variants.find(v =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      );
    }

    const moneyFormat = getMoneyFormat(section);

    function updateUI(variant, options) {
      const updatePrices = !options || options.updatePrices !== false;

      if (!variant) {
        if (atcBtn) { atcBtn.disabled = true; }
        if (atcText) atcText.textContent = 'Unavailable';
        return;
      }

      // Price — keep Liquid/converted prices on first paint; update on variant change
      if (updatePrices) {
        if (priceEl) {
          priceEl.textContent = formatPrice(variant.price, moneyFormat);
          if (variant.compare_at_price > variant.price) {
            priceEl.setAttribute('data-on-sale', '');
          } else {
            priceEl.removeAttribute('data-on-sale');
          }
        }
        if (comparePriceEl) {
          if (variant.compare_at_price > variant.price) {
            comparePriceEl.textContent = formatPrice(variant.compare_at_price, moneyFormat);
            comparePriceEl.style.display = '';
          } else {
            comparePriceEl.style.display = 'none';
          }
        }
      } else if (comparePriceEl) {
        comparePriceEl.style.display = variant.compare_at_price > variant.price ? '' : 'none';
      }

      // Hidden variant ID
      if (variantIdEl) variantIdEl.value = variant.id;

      // ATC button
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        if (atcText) {
          atcText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }
      }

      // Featured image
      if (variant.featured_image && gallery) {
        const imgSrc = variant.featured_image.src;
        const allSlides = qsa('.nb-gallery__slide', section);
        const matchIndex = allSlides.findIndex(slide => {
          const img = slide.querySelector('img');
          return img && img.src.includes(imgSrc.split('?')[0].split('/').pop().split('_')[0]);
        });
        if (matchIndex >= 0) gallery.goTo(matchIndex);
      }

      // URL update (no page reload)
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({ variantId: variant.id }, '', url.toString());
      }
    }

    // Listen for option changes
    form.addEventListener('change', e => {
      // Update displayed labels
      if (e.target.name && e.target.name.includes('Color')) {
        if (colorValue) colorValue.textContent = e.target.value;
      }
      if (e.target.name && e.target.name.includes('Size')) {
        if (sizeValue) sizeValue.textContent = e.target.value;
      }

      const selected = getSelectedOptions();
      const variant  = findVariant(selected);
      updateUI(variant, { updatePrices: true });
    });

    function refreshPrices() {
      const selected = getSelectedOptions();
      const variant  = findVariant(selected);
      if (variant) updateUI(variant, { updatePrices: true });
    }

    // Re-format when a currency converter switches currency
    ['currency:changed', 'currency.changed'].forEach(function (eventName) {
      document.addEventListener(eventName, refreshPrices);
    });

    // Init — preserve server-rendered (and currency-converted) prices
    const selected = getSelectedOptions();
    const variant  = findVariant(selected);
    if (variant) updateUI(variant, { updatePrices: false });
  }

  // ── Quantity stepper ──────────────────────────────────────────
  function initQty(section) {
    const input   = qs('[data-qty-input]', section);
    const btnMinus = qs('[data-qty-minus]', section);
    const btnPlus  = qs('[data-qty-plus]', section);
    if (!input) return;

    function clamp(val) { return Math.max(1, parseInt(val) || 1); }

    if (btnMinus) btnMinus.addEventListener('click', () => {
      input.value = clamp(input.value) - 1 < 1 ? 1 : clamp(input.value) - 1;
    });
    if (btnPlus) btnPlus.addEventListener('click', () => {
      input.value = clamp(input.value) + 1;
    });
    input.addEventListener('change', () => { input.value = clamp(input.value); });
  }

  // ── Add to cart (AJAX) ────────────────────────────────────────
  function initATC(section) {
    if (section.dataset.previewMode !== undefined) return;

    const form   = qs('.nb-product__form', section);
    const atcBtn = qs('[data-add-to-cart]', section);
    if (!form || !atcBtn) return;

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (atcBtn.disabled) return;

      atcBtn.classList.add('nb-btn--loading');
      atcBtn.disabled = true;

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error('Add to cart failed');

        // Trigger cart update event (theme picks this up)
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Brief success state
        const atcText = qs('[data-add-to-cart-text]', section);
        atcBtn.classList.remove('nb-btn--loading');

        const original = atcText ? atcText.textContent : '';
        if (atcText) atcText.textContent = '✓ Added!';
        atcBtn.style.background = '#7AAF7A';

        setTimeout(() => {
          atcBtn.disabled = false;
          atcBtn.style.background = '';
          if (atcText) atcText.textContent = original || 'Add to Cart';
        }, 2000);

      } catch (err) {
        atcBtn.classList.remove('nb-btn--loading');
        atcBtn.disabled = false;
        console.error('NB ATC error:', err);
      }
    });
  }

  // ── Buy Now (checkout redirect) ───────────────────────────────
  function initBuyNow(section) {
    if (section.dataset.previewMode !== undefined) return;

    const btn  = qs('[data-buy-now]', section);
    const form = qs('.nb-product__form', section);
    if (!btn || !form) return;

    btn.addEventListener('click', async function () {
      const variantId = qs('[data-variant-id]', form);
      const qtyInput    = qs('[data-qty-input]', section);
      if (!variantId || !variantId.value) return;

      btn.disabled = true;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId.value, quantity: qty })
        });
        window.location.href = '/checkout';
      } catch (err) {
        btn.disabled = false;
        console.error('NB Buy Now error:', err);
      }
    });
  }

  // ── Bootstrap ─────────────────────────────────────────────────
  function init() {
    const sections = qsa('.nb-product');
    sections.forEach(section => {
      const gallery = initGallery(section);
      initVariants(section, gallery);
      initQty(section);
      initATC(section);
      initBuyNow(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init for Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector('.nb-product');
    if (!section) return;
    const gallery = initGallery(section);
    initVariants(section, gallery);
    initQty(section);
    initATC(section);
    initBuyNow(section);
  });

})();
